/**
 * Error Monitoring Service
 * Centralized error capture and reporting with provider abstraction
 *
 * USAGE:
 * ```typescript
 * import { captureError, setErrorMonitoringUser } from "@/services/errorMonitoringService";
 *
 * try {
 *   // risky operation
 * } catch (error) {
 *   captureError(error, { context: "user-action", caseId: "123" });
 * }
 *
 * // Set user context after auth
 * setErrorMonitoringUser("user-123");
 * ```
 *
 * INITIALIZATION:
 * Call initErrorMonitoringIfNeeded() once at app startup
 */

import { ERROR_MONITORING_CONFIG, isErrorMonitoringEnabled } from "@/config/errorMonitoringConfig";

/**
 * Module-level flag to track initialization
 */
let isInitialized = false;

/**
 * Cached Sentry module reference — populated during init so public capture
 * functions can remain synchronous.
 */
let _sentry: typeof import("@sentry/react") | null = null;

/**
 * Initialize error monitoring if enabled
 * This function is safe to call multiple times (idempotent)
 *
 * For Sentry: Initializes the Sentry SDK with DSN and config and caches the
 * module reference so subsequent capture calls are synchronous.
 */
export async function initErrorMonitoringIfNeeded(): Promise<void> {
  // Skip if already initialized
  if (isInitialized) {
    return;
  }

  // Skip if not in browser
  if (typeof window === "undefined") {
    isInitialized = true;
    return;
  }

  // Skip if error monitoring is disabled
  if (!isErrorMonitoringEnabled()) {
    if (import.meta.env.DEV) console.info("[ErrorMonitoring] Disabled by configuration");
    isInitialized = true;
    return;
  }

  try {
    if (ERROR_MONITORING_CONFIG.provider === "sentry") {
      const Sentry = await import("@sentry/react");
      const { scrubEvent, scrubBreadcrumb } = await import("./sentryScrub");
      Sentry.init({
        dsn: ERROR_MONITORING_CONFIG.dsn,
        environment: ERROR_MONITORING_CONFIG.environment,
        release: import.meta.env.VITE_BUILD_TAG || undefined,
        sendDefaultPii: false,
        sampleRate: ERROR_MONITORING_CONFIG.sampleRate,
        tracesSampleRate: ERROR_MONITORING_CONFIG.tracesSampleRate,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: ERROR_MONITORING_CONFIG.replaysOnErrorSampleRate,
        ignoreErrors: [
          // Meta (Instagram/Facebook) in-app browser injects telemetry that
          // calls window.webkit.messageHandlers on page-hide. That WKWebView
          // bridge is undefined in some iOS/app-version combos, throwing a
          // TypeError our global onerror handler captures. Not our code — the
          // symbols below appear nowhere in this repo. See TL-WEBSITE-2.
          /window\.webkit\.messageHandlers/,
          "sendDataToNative",
          "sendPageHideMessage",
        ],
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
        ],
        beforeSend: (event) => scrubEvent(event),
        beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
      });
      // Cache the module so capture functions are synchronous
      _sentry = Sentry;
    }

    isInitialized = true;
    if (import.meta.env.DEV) console.info("[ErrorMonitoring] Initialized", {
      provider: ERROR_MONITORING_CONFIG.provider,
      environment: ERROR_MONITORING_CONFIG.environment,
    });
  } catch (error) {
    console.warn("[ErrorMonitoring] Failed to initialize:", error);
    isInitialized = true; // Mark as initialized to prevent retry loops
  }
}

/**
 * Capture an error for monitoring
 * Does not rethrow - safe to use in catch blocks
 *
 * @param error - Error to capture (Error object, string, or unknown)
 * @param context - Additional context to attach to the error
 *
 * @example
 * captureError(new Error("Something failed"), {
 *   userId: "123",
 *   caseId: "abc",
 *   action: "create_filing"
 * });
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  // Always log to console for debugging
  console.error("[ErrorMonitoring]", error, context);

  // Skip if error monitoring not enabled or Sentry not cached
  if (!isErrorMonitoringEnabled() || !isInitialized || !_sentry) {
    return;
  }

  try {
    if (ERROR_MONITORING_CONFIG.provider === "sentry") {
      if (context) _sentry.setContext("additional", context);
      _sentry.captureException(error);
    }
  } catch (captureErr) {
    // Never let error monitoring break the app
    console.error("[ErrorMonitoring] Failed to capture error:", captureErr);
  }
}

/**
 * Capture a message (non-error) for monitoring
 * Useful for logging important events or warnings
 *
 * @param message - Message to capture
 * @param level - Severity level
 * @param context - Additional context
 */
export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
  context?: Record<string, unknown>
): void {
  console[level === "error" ? "error" : level === "warning" ? "warn" : "log"](
    "[ErrorMonitoring]",
    message,
    context
  );

  // Skip if error monitoring not enabled or Sentry not cached
  if (!isErrorMonitoringEnabled() || !isInitialized || !_sentry) {
    return;
  }

  try {
    if (ERROR_MONITORING_CONFIG.provider === "sentry") {
      if (context) _sentry.setContext("additional", context);
      _sentry.captureMessage(message, level);
    }
  } catch (error) {
    console.error("[ErrorMonitoring] Failed to capture message:", error);
  }
}

/**
 * Set the current user context for error monitoring
 * Call this after user authentication
 *
 * @param userId - User ID (null to clear)
 * @param _userData - Ignored — only UID is forwarded to Sentry, never PII
 *
 * @example
 * setErrorMonitoringUser("user-123");
 */
export function setErrorMonitoringUser(
  userId: string | null,
  _userData?: Record<string, unknown>
): void {
  // Skip if error monitoring not enabled or Sentry not cached
  if (!isErrorMonitoringEnabled() || !isInitialized || !_sentry) {
    return;
  }

  try {
    if (ERROR_MONITORING_CONFIG.provider === "sentry") {
      _sentry.setUser(userId ? { id: userId } : null);
    }
  } catch (error) {
    console.error("[ErrorMonitoring] Failed to set user:", error);
  }
}

/**
 * Add a breadcrumb for debugging
 * Breadcrumbs help trace the events leading up to an error
 *
 * @param message - Breadcrumb message
 * @param category - Category for grouping
 * @param data - Additional data
 */
export function addBreadcrumb(
  message: string,
  category: string = "custom",
  data?: Record<string, unknown>
): void {
  // Skip if error monitoring not enabled or Sentry not cached
  if (!isErrorMonitoringEnabled() || !isInitialized || !_sentry) {
    return;
  }

  try {
    if (ERROR_MONITORING_CONFIG.provider === "sentry") {
      _sentry.addBreadcrumb({ message, category, level: "info", data });
    }
  } catch (error) {
    console.error("[ErrorMonitoring] Failed to add breadcrumb:", error);
  }
}

/**
 * Check if error monitoring is ready
 */
export function isErrorMonitoringReady(): boolean {
  return isInitialized && isErrorMonitoringEnabled();
}
